import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { ShoppingCartCounter } from '../../atoms/ShoppingCartCounter';
import { Typography } from '../../atoms/Typography';
import { InfoFilled as InfoFilledIcon } from '../../icons';
import { LoadingSpinner } from '../../layouts/LoadingSpinner';

export interface CheckoutButtonProps {
  basketItemCount: number;
  buttonLabel: string;
  isDisabled: boolean;
  isLoading: boolean;
  isTotalOverMinOrder: boolean;
  localisedBasketTotal: string;
  localisedMinOrder: string;
  onCheckout: () => void;
  translate: TranslateFunction;
}

const useStyles = makeStyles((theme: Theme) => ({
  checkoutButton: {
    backgroundColor: theme.palette.common.white,
    border: 'none',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    bottom: 0,
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: '56px 1fr',
    minHeight: 'unset',
    padding: theme.spacing(1, 1, 1, 0),
    position: 'fixed',
    right: 0,
    width: '100%',
    zIndex: theme.zIndex.appBar,

    '& .buttonState': {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      width: '100%',
    },
    '& .fd-button__primary': {
      padding: theme.spacing(1.5, 2),
    },
  },
  buttonContent: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    width: '100%',

    '& .fd-text em': {
      color: theme.palette.primary.contrastText,
    },

    '.loading &, .disabled &': {
      backgroundColor: theme.palette.grey[200],

      '& .fd-text em, & .fd-icon__spinner': {
        color: theme.palette.action.disabled,
      },
    },
  },
  minOrderWarning: {
    alignItems: 'center',
    color: theme.palette.grey[800],
    display: 'flex',
    flexFlow: 'row nowrap',

    '& svg': {
      color: theme.palette.info.main,
      marginRight: theme.spacing(1),
    },
  },
  loadingSpinner: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    left: 56, // shopping cart counter width 56
    position: 'absolute',
    right: 8, // button padding 8
  },

  [theme.breakpoints.up('sm')]: {
    checkoutButton: {
      border: `1px solid ${theme.palette.grey[200]}`,
      borderBottom: 'transparent',
      boxSizing: 'border-box',
      right: theme.spacing(1),
      width: '360px',

      '& .fd-button__primary': {
        padding: theme.spacing(2.5, 3),
      },
    },
  },
}));

export function CheckoutButton(props: CheckoutButtonProps): JSX.Element {
  const classes = useStyles();
  const {
    basketItemCount,
    buttonLabel,
    isDisabled,
    isLoading,
    isTotalOverMinOrder,
    localisedBasketTotal,
    localisedMinOrder,
    onCheckout,
    translate,
  } = props;

  const checkoutButtonStyles = clsx(
    classes.checkoutButton,
    'fd-button fd-button__primary',
    isLoading && 'loading',
    isDisabled && 'disabled',
  );
  const itemCounterStyles = clsx(
    classes.itemCounter,
    isLoading && 'loading',
    isDisabled && 'disabled',
  );

  const renderButtonContent = (): JSX.Element => {
    if (isTotalOverMinOrder) {
      return (
        <Typography variant="h3">
          <em>{buttonLabel}</em>
        </Typography>
      );
    }

    return (
      <div className={classes.minOrderWarning}>
        <InfoFilledIcon size="xs" />
        <Typography variant="body2">
          {translate('Menu_Basket_MinimumOrder')} {localisedMinOrder}
        </Typography>
      </div>
    );
  };

  return (
    <button
      className={checkoutButtonStyles}
      disabled={isDisabled}
      onClick={onCheckout}
      type="button"
    >
      <ShoppingCartCounter
        count={basketItemCount}
        className={itemCounterStyles}
        size="m"
      />

      <div className={classes.buttonContent}>
        {renderButtonContent()}

        <Typography variant="h3">
          <em>{localisedBasketTotal}</em>
        </Typography>

        {isLoading && (
          <LoadingSpinner className={classes.loadingSpinner} variant="inline" />
        )}
      </div>
    </button>
  );
}
